import { ReactNode } from 'react';
import {
  GROUPS,
  CATEGORY_ORDER,
  CATEGORY_VALUES,
} from '../constants/grouping-constants';
import { GroupKey } from '../types';
import { MaxApplicationsPerRole } from '@r-and-a-shared-ui/common-forms-ui';
import { ACCREDITATION_ROLES } from 'libs/utils/src/lib/player-portals/constants/pp-accreditation-roles';

const getSubCategory = (row: any): string =>
  typeof row?.subCategory === 'string' ? row.subCategory : '';

const countPlayerSupportByType = (rows: any[]) => {
  const values = rows.map(getSubCategory);
  return {
    coach: values.filter(
      v => v === ACCREDITATION_ROLES.playerSupportCoach.label,
    ).length,
    medical: values.filter(
      v => v === ACCREDITATION_ROLES.playerSupportMedical.label,
    ).length,
    manager: values.filter(
      v => v === ACCREDITATION_ROLES.playerSupportManager.label,
    ).length,
    translator: values.filter(
      v => v === ACCREDITATION_ROLES.playerSupportTranslator.label,
    ).length,
  };
};

const getMaxForRole = (
  maxNumberOfApplicationsPerRole: MaxApplicationsPerRole[] | undefined,
  roleNames: string[],
): number | undefined => {
  const roleIndexes = roleNames.map(name => {
    const role = Object.values(ACCREDITATION_ROLES).find(r => r.label === name);
    return role?.value;
  });

  const match = maxNumberOfApplicationsPerRole?.find(item => {
    const roleIndex = Number(item?.item?.value);
    return roleIndexes.includes(roleIndex);
  });

  const max = Number(match?.key);
  return Number.isFinite(max) ? max : undefined;
};

const findGroupForRow = (row: any): GroupKey => {
  const value = row?.subCategory;

  if (typeof value !== 'string' || value.trim().length === 0) {
    return GROUPS.playerAndCaddie;
  }

  const matched = CATEGORY_ORDER.find(group =>
    CATEGORY_VALUES[group].includes(value),
  );

  return matched ?? GROUPS.playerAndCaddie;
};

const groupVisibleRows = (rows: any[]): Record<GroupKey, any[]> => {
  const grouped: Record<GroupKey, any[]> = {
    playerAndCaddie: [],
    supportTeam: [],
    guests: [],
    children: [],
  };

  rows.forEach(row => {
    grouped[findGroupForRow(row)].push(row);
  });

  return grouped;
};

const renderSectionLabel = (
  group: GroupKey,
  rows: any[],
  maxNumberOfApplicationsPerRole?: MaxApplicationsPerRole[],
): ReactNode => {
  if (group === GROUPS.playerAndCaddie) {
    return null;
  }

  if (group === GROUPS.supportTeam) {
    const counts = countPlayerSupportByType(rows);

    const coachLimit = getMaxForRole(maxNumberOfApplicationsPerRole, [
      ACCREDITATION_ROLES.playerSupportCoach.label,
    ]);
    const medicalLimit = getMaxForRole(maxNumberOfApplicationsPerRole, [
      ACCREDITATION_ROLES.playerSupportMedical.label,
    ]);
    const managerLimit = getMaxForRole(maxNumberOfApplicationsPerRole, [
      ACCREDITATION_ROLES.playerSupportManager.label,
    ]);
    const translatorLimit = getMaxForRole(maxNumberOfApplicationsPerRole, [
      ACCREDITATION_ROLES.playerSupportTranslator.label,
    ]);

    return (
      <span>
        Player Support Team: &nbsp; Coaches:
        <strong>
          {counts.coach}/{coachLimit}
        </strong>
        Medical/Physio:
        <strong>
          {counts.medical}/{medicalLimit}
        </strong>
        Manager:
        <strong>
          {counts.manager}/{managerLimit}
        </strong>
        Translator:
        <strong>
          {counts.translator}/{translatorLimit}
        </strong>
      </span>
    );
  }
  if (group === GROUPS.guests) {
    const guestLimit = getMaxForRole(maxNumberOfApplicationsPerRole, [
      ACCREDITATION_ROLES.playerGuest.label,
    ]);

    return (
      <span>
        Player Guest:
        <strong>
          {rows.length}/{guestLimit}
        </strong>{' '}
      </span>
    );
  }
  const childLimit = getMaxForRole(maxNumberOfApplicationsPerRole, [
    ACCREDITATION_ROLES.playerChildren.label,
  ]);
  return (
    <span>
      Player Children:
      <strong>
        {Number(childLimit) >= 0 ? `${childLimit}` : 'Unlimited'}
      </strong>{' '}
    </span>
  );
};

export { groupVisibleRows, renderSectionLabel };
